<div class="min-h-screen bg-base-100 text-base-content pt-12 pb-12">
  <div class="container mx-auto px-4 py-6">
    <!-- Header Section -->
    <div class="mb-6">
      <nav class="daisy-breadcrumbs text-xs sm:text-sm mb-3">
        <ul>
          <li><a routerLink="/" class="text-base-content/60 hover:text-primary">Home</a></li>
          <li><a routerLink="/courses" class="text-base-content/60 hover:text-primary">Courses</a></li>
          <li class="font-medium text-base-content">Payment</li>
        </ul>
      </nav>
      <h1 class="text-3xl font-bold mb-2 text-base-content">Complete Your Payment</h1>
      <p class="text-base-content/70 text-base">Select a payment method to continue</p>
    </div>

    @if (isLoading) {
      <!-- Skeleton Payment Method Selector -->
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div class="daisy-skeleton h-20 rounded-xl"></div>
        <div class="daisy-skeleton h-20 rounded-xl"></div>
        <div class="daisy-skeleton h-20 rounded-xl"></div>
      </div>

      <!-- Skeleton Main Content Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <!-- Left: Order Details Skeleton -->
        <div class="daisy-card daisy-card-bordered bg-base-100 shadow-lg p-6 space-y-4">
          <div class="daisy-skeleton h-20 w-full rounded-lg"></div>
          <div class="daisy-skeleton h-16 w-full rounded-lg"></div>
          <div class="space-y-3 pt-2">
            <div class="daisy-skeleton h-5 w-full rounded"></div>
            <div class="daisy-skeleton h-5 w-full rounded"></div>
            <div class="daisy-skeleton h-5 w-full rounded"></div>
            <div class="daisy-skeleton h-5 w-3/4 rounded"></div>
          </div>
        </div>

        <!-- Right: Payment Form Skeleton -->
        <div class="daisy-card daisy-card-bordered bg-base-100 shadow-lg p-6 space-y-4">
          <div class="daisy-skeleton h-7 w-40 rounded"></div>
          <div class="space-y-3">
            <div class="daisy-skeleton h-10 w-full rounded-lg"></div>
            <div class="daisy-skeleton h-10 w-full rounded-lg"></div>
            <div class="daisy-skeleton h-10 w-full rounded-lg"></div>
            <div class="daisy-skeleton h-24 w-full rounded-lg"></div>
            <div class="daisy-skeleton h-12 w-full rounded-lg"></div>
          </div>
        </div>
      </div>
    } @else {
      @if (!isPayHereEnabled && !isBankTransferEnabled) {
        <div class="daisy-alert daisy-alert-error mb-6">
          <i class="fa-solid fa-triangle-exclamation text-lg"></i>
          <span>Payment is currently unavailable. Please contact the academy administrator.</span>
        </div>
      }

    <!-- Payment Method Selector -->
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
      <!-- Bank Transfer -->
      @if (isBankTransferEnabled) {
        <button
          type="button"
          class="relative flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all"
          [class.border-primary]="selectedPaymentMethod === PaymentOptions.BANK_TRANSFER"
          [class.bg-primary/10]="selectedPaymentMethod === PaymentOptions.BANK_TRANSFER"
          [class.shadow-md]="selectedPaymentMethod === PaymentOptions.BANK_TRANSFER"
          [class.border-base-300]="selectedPaymentMethod !== PaymentOptions.BANK_TRANSFER"
          [class.bg-base-100]="selectedPaymentMethod !== PaymentOptions.BANK_TRANSFER"
          [class.hover:border-primary/50]="selectedPaymentMethod !== PaymentOptions.BANK_TRANSFER"
          (click)="selectPaymentMethod(PaymentOptions.BANK_TRANSFER)"
        >
          <div
            class="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors"
            [class.bg-primary]="selectedPaymentMethod === PaymentOptions.BANK_TRANSFER"
            [class.text-primary-content]="selectedPaymentMethod === PaymentOptions.BANK_TRANSFER"
            [class.bg-primary/10]="selectedPaymentMethod !== PaymentOptions.BANK_TRANSFER"
            [class.text-primary]="selectedPaymentMethod !== PaymentOptions.BANK_TRANSFER"
          >
            <i class="fa-solid fa-building-columns text-lg"></i>
          </div>
          <div class="min-w-0">
            <p
              class="font-semibold text-sm m-0"
              [class.text-primary]="selectedPaymentMethod === PaymentOptions.BANK_TRANSFER"
              [class.text-base-content]="selectedPaymentMethod !== PaymentOptions.BANK_TRANSFER"
            >
              Bank Transfer
            </p>
            <p class="text-xs text-base-content/60 m-0 mt-0.5">
              Transfer & upload receipt
            </p>
          </div>
          @if (selectedPaymentMethod === PaymentOptions.BANK_TRANSFER) {
            <div
              class="absolute top-2 right-2 w-5 h-5 bg-primary text-primary-content rounded-full flex items-center justify-center"
            >
              <i class="fa-solid fa-check text-xs"></i>
            </div>
          }
        </button>
      }

      <!-- PayHere Online Payment -->
      @if (isPayHereEnabled) {
        <button
          type="button"
          class="relative flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all"
          [class.border-primary]="selectedPaymentMethod === PaymentOptions.PAYHERE"
          [class.bg-primary/10]="selectedPaymentMethod === PaymentOptions.PAYHERE"
          [class.shadow-md]="selectedPaymentMethod === PaymentOptions.PAYHERE"
          [class.border-base-300]="selectedPaymentMethod !== PaymentOptions.PAYHERE"
          [class.bg-base-100]="selectedPaymentMethod !== PaymentOptions.PAYHERE"
          [class.hover:border-primary/50]="selectedPaymentMethod !== PaymentOptions.PAYHERE"
          (click)="selectPaymentMethod(PaymentOptions.PAYHERE)"
        >
          <div
            class="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors"
            [class.bg-primary]="selectedPaymentMethod === PaymentOptions.PAYHERE"
            [class.text-primary-content]="selectedPaymentMethod === PaymentOptions.PAYHERE"
            [class.bg-primary/10]="selectedPaymentMethod !== PaymentOptions.PAYHERE"
            [class.text-primary]="selectedPaymentMethod !== PaymentOptions.PAYHERE"
          >
            <i class="fa-solid fa-credit-card text-lg"></i>
          </div>
          <div class="min-w-0">
            <p
              class="font-semibold text-sm m-0"
              [class.text-primary]="selectedPaymentMethod === PaymentOptions.PAYHERE"
              [class.text-base-content]="selectedPaymentMethod !== PaymentOptions.PAYHERE"
            >
              PayHere
            </p>
            <p class="text-xs text-base-content/60 m-0 mt-0.5">
              Card / Online payment
            </p>
          </div>
          @if (selectedPaymentMethod === PaymentOptions.PAYHERE) {
            <div
              class="absolute top-2 right-2 w-5 h-5 bg-primary text-primary-content rounded-full flex items-center justify-center"
            >
              <i class="fa-solid fa-check text-xs"></i>
            </div>
          }
        </button>
      }

      <!-- Card (disabled / coming soon) -->
      <div
        class="relative flex items-center gap-4 p-4 rounded-xl border-2 border-base-300 bg-base-200/50 text-left opacity-50 cursor-not-allowed select-none"
        title="Coming soon"
      >
        <div
          class="w-11 h-11 rounded-xl bg-base-300 text-base-content/40 flex items-center justify-center flex-shrink-0"
        >
          <i class="fa-solid fa-wallet text-lg"></i>
        </div>
        <div class="min-w-0">
          <p class="font-semibold text-sm m-0 text-base-content/50">Online Wallet</p>
          <p class="text-xs text-base-content/40 m-0 mt-0.5">E-wallet payment</p>
        </div>
        <span
          class="absolute top-2 right-2 text-xs daisy-badge daisy-badge-ghost text-base-content/60 leading-tight"
        >Soon</span>
      </div>
    </div>

    <!-- Bank Transfer Flow -->
    @if (selectedPaymentMethod === PaymentOptions.BANK_TRANSFER && isBankTransferEnabled) {
      <!-- Progress Steps Indicator -->
      <div class="mb-8">
        <div class="bg-primary/10 border-l-4 border-primary rounded-lg p-4 shadow-sm">
          <div class="flex items-center justify-between mb-3">
            <h4 class="text-primary font-semibold text-lg m-0">Payment Process</h4>
            <span class="text-sm text-primary/80 font-medium">3 Simple Steps</span>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="flex items-start space-x-3">
              <div class="flex-shrink-0 w-8 h-8 bg-primary text-primary-content rounded-full flex items-center justify-center font-bold">1</div>
              <div>
                <p class="font-semibold text-sm m-0 text-base-content">Review Order</p>
                <p class="text-xs text-base-content/60 m-0">Check amount and items</p>
              </div>
            </div>
            <div class="flex items-start space-x-3">
              <div class="flex-shrink-0 w-8 h-8 bg-primary text-primary-content rounded-full flex items-center justify-center font-bold">2</div>
              <div>
                <p class="font-semibold text-sm m-0 text-base-content">Transfer Payment</p>
                <p class="text-xs text-base-content/60 m-0">Send to provided account</p>
              </div>
            </div>
            <div class="flex items-start space-x-3">
              <div class="flex-shrink-0 w-8 h-8 bg-primary text-primary-content rounded-full flex items-center justify-center font-bold">3</div>
              <div>
                <p class="font-semibold text-sm m-0 text-base-content">Upload Receipt</p>
                <p class="text-xs text-base-content/60 m-0">Attach payment proof</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <!-- Left Column: Order Details -->
        <div>
          <div class="flex items-center mb-4">
            <div class="w-8 h-8 bg-primary text-primary-content rounded-full flex items-center justify-center font-bold mr-3 flex-shrink-0">1</div>
            <h3 class="text-xl font-semibold m-0 text-base-content">Order Details</h3>
          </div>

          @if (orderDetails) {
            <div class="daisy-card daisy-card-bordered bg-base-100 shadow-lg">
              <div class="daisy-card-body p-6">
                <!-- Invoice Number Highlighted -->
                <div class="bg-warning/10 border-2 border-warning/40 rounded-lg p-4 mb-4">
                  <div class="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p class="text-xs text-warning font-semibold uppercase tracking-wide m-0 mb-1">Invoice Number</p>
                      <p class="text-xl font-bold text-base-content m-0 tracking-wider font-mono">
                        {{ orderDetails.orderId }}
                      </p>
                    </div>
                    <div class="flex items-start gap-2 bg-warning/20 border border-warning/30 rounded-lg px-3 py-2 max-w-xs">
                      <i class="fa-solid fa-triangle-exclamation text-warning text-sm flex-shrink-0 mt-0.5"></i>
                      <p class="text-xs text-base-content/80 font-medium m-0">Include this invoice number as the <strong>transaction reference</strong> when making your transfer</p>
                    </div>
                  </div>
                </div>

                <!-- Amount Due Highlighted -->
                <div class="bg-error/10 border border-error/20 rounded-lg p-4 mb-4">
                  <div class="flex justify-between items-center">
                    <div>
                      <span class="text-base-content/80 font-semibold block">
                        {{ orderDetails.paymentPlan === 'INSTALLMENT' ? 'Amount Due (Installment ' + (currentInstallmentNumber || 1) + ')' : 'Amount Due' }}
                      </span>
                      @if (orderDetails.paymentPlan === 'INSTALLMENT') {
                        <span class="text-xs text-base-content/60">Total Course Fee: {{ orderDetails.currency || 'LKR' }} {{ orderDetails.totalAmount | number: '1.2-2' }}</span>
                      }
                    </div>
                    <span class="text-2xl font-bold text-error">
                      {{ orderDetails.currency || 'LKR' }} {{ payableAmount | number: '1.2-2' }}
                    </span>
                  </div>
                </div>

                <!-- Order Information -->
                <div class="space-y-3">
                  <div class="flex justify-between py-2 border-b border-base-200">
                    <span class="text-base-content/60">Invoice #</span>
                    <span class="font-mono font-semibold text-base-content">{{ orderDetails.orderId }}</span>
                  </div>
                  <div class="flex justify-between py-2 border-b border-base-200">
                    <span class="text-base-content/60">Courses</span>
                    <span class="font-medium text-base-content">{{ orderDetails.items.length }} {{ orderDetails.items.length === 1 ? 'course' : 'courses' }}</span>
                  </div>
                  <div class="flex justify-between py-2 border-b border-base-200">
                    <span class="text-base-content/60">Total Amount</span>
                    <span class="font-medium text-base-content">{{ orderDetails.currency || 'LKR' }} {{ orderDetails.totalAmount | number: '1.2-2' }}</span>
                  </div>
                  <div class="flex justify-between py-2">
                    <span class="text-base-content/60">Status</span>
                    <span class="daisy-badge daisy-badge-primary">Pending</span>
                  </div>
                </div>
              </div>
            </div>
          }

          <!-- Quick Help Card -->
          <div class="mt-4 bg-base-200/60 border border-base-300 rounded-lg p-4">
            <h5 class="font-semibold text-sm mb-2 flex items-center text-base-content">
              <i class="fa-solid fa-circle-info mr-2 text-primary"></i>
              Important Notes
            </h5>
            <ul class="text-sm text-base-content/70 space-y-1 m-0 list-disc pl-5">
              <li>Include invoice reference in transfer notes</li>
              <li>Payment verified within 24 hours</li>
              <li>Accepted formats: PNG, JPG, PDF, Word, Excel</li>
            </ul>
          </div>
        </div>

        <!-- Right Column: Payment Form (reuses shared billing-form) -->
        <div>
          <div class="flex items-center mb-4">
            <div class="w-8 h-8 bg-primary text-primary-content rounded-full flex items-center justify-center font-bold mr-3 flex-shrink-0">2</div>
            <h3 class="text-xl font-semibold m-0 text-base-content">Complete Payment</h3>
          </div>

          <div class="daisy-card daisy-card-bordered bg-base-100 shadow-lg">
            <div class="daisy-card-body p-6">
              <black-billing-form
                [bankAccounts]="tenantBankAccounts"
                [amount]="payableAmount"
                [currency]="orderDetails?.currency || 'LKR'"
                [referenceNumber]="orderDetails?.orderId"
                (successPayment)="onBankPaymentSuccess($event)"
              ></black-billing-form>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- PayHere Payment Flow -->
    @if (selectedPaymentMethod === PaymentOptions.PAYHERE && isPayHereEnabled) {
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <!-- Left Column: Order Details -->
        <div>
          <div class="flex items-center mb-4">
            <div class="w-8 h-8 bg-primary text-primary-content rounded-full flex items-center justify-center font-bold mr-3 flex-shrink-0">1</div>
            <h3 class="text-xl font-semibold m-0 text-base-content">Order Details</h3>
          </div>

          @if (orderDetails) {
            <div class="daisy-card daisy-card-bordered bg-base-100 shadow-lg">
              <div class="daisy-card-body p-6">
                <div class="bg-warning/10 border-2 border-warning/40 rounded-lg p-4 mb-4">
                  <div class="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p class="text-xs text-warning font-semibold uppercase tracking-wide m-0 mb-1">Invoice Number</p>
                      <p class="text-xl font-bold text-base-content m-0 tracking-wider font-mono">
                        {{ orderDetails.orderId }}
                      </p>
                    </div>
                  </div>
                </div>

                <div class="bg-error/10 border border-error/20 rounded-lg p-4 mb-4">
                  <div class="flex justify-between items-center">
                    <div>
                      <span class="text-base-content/80 font-semibold block">
                        {{ orderDetails.paymentPlan === 'INSTALLMENT' ? 'Amount Due (Installment ' + (currentInstallmentNumber || 1) + ')' : 'Amount Due' }}
                      </span>
                      @if (orderDetails.paymentPlan === 'INSTALLMENT') {
                        <span class="text-xs text-base-content/60">Total Course Fee: {{ orderDetails.currency || 'LKR' }} {{ orderDetails.totalAmount | number: '1.2-2' }}</span>
                      }
                    </div>
                    <span class="text-2xl font-bold text-error">
                      {{ orderDetails.currency || 'LKR' }} {{ payableAmount | number: '1.2-2' }}
                    </span>
                  </div>
                </div>

                <div class="space-y-3">
                  <div class="flex justify-between py-2 border-b border-base-200">
                    <span class="text-base-content/60">Invoice #</span>
                    <span class="font-mono font-semibold text-base-content">{{ orderDetails.orderId }}</span>
                  </div>
                  <div class="flex justify-between py-2 border-b border-base-200">
                    <span class="text-base-content/60">Courses</span>
                    <span class="font-medium text-base-content">{{ orderDetails.items.length }} {{ orderDetails.items.length === 1 ? 'course' : 'courses' }}</span>
                  </div>
                  <div class="flex justify-between py-2 border-b border-base-200">
                    <span class="text-base-content/60">Total Amount</span>
                    <span class="font-medium text-base-content">{{ orderDetails.currency || 'LKR' }} {{ orderDetails.totalAmount | number: '1.2-2' }}</span>
                  </div>
                  <div class="flex justify-between py-2">
                    <span class="text-base-content/60">Status</span>
                    <span class="daisy-badge daisy-badge-primary">Pending</span>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>

        <!-- Right Column: Billing Form & Pay Button -->
        <div>
          <div class="flex items-center mb-4">
            <div class="w-8 h-8 bg-primary text-primary-content rounded-full flex items-center justify-center font-bold mr-3 flex-shrink-0">2</div>
            <h3 class="text-xl font-semibold m-0 text-base-content">Billing Details</h3>
          </div>

          <div class="daisy-card daisy-card-bordered bg-base-100 shadow-lg">
            <div class="daisy-card-body p-6">
              @if (payhereError) {
                <div class="daisy-alert daisy-alert-error mb-4">
                  <p class="text-sm m-0">{{ payhereError }}</p>
                </div>
              }

              <form [formGroup]="billingForm">
                <div class="grid grid-cols-1 gap-4">
                  <div class="daisy-form-control">
                    <label class="daisy-label"><span class="daisy-label-text font-medium">Full Name</span></label>
                    <input type="text" formControlName="name" class="daisy-input daisy-input-bordered w-full" placeholder="Full Name" />
                    @if (billingForm.controls.name.touched && billingForm.controls.name.invalid) {
                      <span class="text-xs text-error mt-1">Name is required</span>
                    }
                  </div>
                  <div class="daisy-form-control">
                    <label class="daisy-label"><span class="daisy-label-text font-medium">Address Line 1</span></label>
                    <input type="text" formControlName="addressLine1" class="daisy-input daisy-input-bordered w-full" placeholder="Address Line 1" />
                    @if (billingForm.controls.addressLine1.touched && billingForm.controls.addressLine1.invalid) {
                      <span class="text-xs text-error mt-1">Address is required</span>
                    }
                  </div>
                  <div class="daisy-form-control">
                    <label class="daisy-label"><span class="daisy-label-text font-medium">Address Line 2</span></label>
                    <input type="text" formControlName="addressLine2" class="daisy-input daisy-input-bordered w-full" placeholder="Address Line 2" />
                    @if (billingForm.controls.addressLine2.touched && billingForm.controls.addressLine2.invalid) {
                      <span class="text-xs text-error mt-1">Address Line 2 is required</span>
                    }
                  </div>
                  <div class="grid grid-cols-2 gap-4">
                    <div class="daisy-form-control">
                      <label class="daisy-label"><span class="daisy-label-text font-medium">City</span></label>
                      <input type="text" formControlName="city" class="daisy-input daisy-input-bordered w-full" placeholder="City" />
                      @if (billingForm.controls.city.touched && billingForm.controls.city.invalid) {
                        <span class="text-xs text-error mt-1">City is required</span>
                      }
                    </div>
                    <div class="daisy-form-control">
                      <label class="daisy-label"><span class="daisy-label-text font-medium">Province</span></label>
                      <input type="text" formControlName="province" class="daisy-input daisy-input-bordered w-full" placeholder="Province" />
                      @if (billingForm.controls.province.touched && billingForm.controls.province.invalid) {
                        <span class="text-xs text-error mt-1">Province is required</span>
                      }
                    </div>
                  </div>
                  <div class="grid grid-cols-2 gap-4">
                    <div class="daisy-form-control">
                      <label class="daisy-label"><span class="daisy-label-text font-medium">Postal Code</span></label>
                      <input type="text" formControlName="postal" class="daisy-input daisy-input-bordered w-full" placeholder="Postal Code" />
                      @if (billingForm.controls.postal.touched && billingForm.controls.postal.invalid) {
                        <span class="text-xs text-error mt-1">Postal code is required</span>
                      }
                    </div>
                    <div class="daisy-form-control">
                      <label class="daisy-label"><span class="daisy-label-text font-medium">Country</span></label>
                      <input type="text" formControlName="country" class="daisy-input daisy-input-bordered w-full" placeholder="Country" />
                      @if (billingForm.controls.country.touched && billingForm.controls.country.invalid) {
                        <span class="text-xs text-error mt-1">Country is required</span>
                      }
                    </div>
                  </div>
                  <div class="daisy-form-control">
                    <label class="daisy-label"><span class="daisy-label-text font-medium">Contact Number</span></label>
                    <input type="text" formControlName="contact" class="daisy-input daisy-input-bordered w-full" placeholder="Contact Number" />
                    @if (billingForm.controls.contact.touched && billingForm.controls.contact.invalid) {
                      <span class="text-xs text-error mt-1">Contact number is required</span>
                    }
                  </div>
                </div>

                <button
                  type="button"
                  class="daisy-btn daisy-btn-primary w-full mt-6"
                  (click)="processPayment()"
                >
                  <i class="fa-solid fa-credit-card mr-2"></i>
                  Pay with PayHere
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    }
  }
  </div>
</div>
